import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ReactDOM from 'react-dom';
import { addLeaveRequest, getEmployees, getLeaveTypes, addEmployee, addLeaveType, getDepartments, getWorkShifts, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { getPositions } from '../../../../redux/Slices/PositionsSlice';
import { getRoles } from '../../../../redux/Slices/RolesSlice';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage } from '../../../../shared/utils.js/utils';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const AddLeaveRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { leaveTypes, departments, positions, workShifts, isLoading, error, success } = useSelector((state) => state.auth);
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });

  // Employee dropdown state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);

  // Leave type dropdown state
  const [leaveTypeDropdownOpen, setLeaveTypeDropdownOpen] = useState(false);
  const [leaveTypeSearchTerm, setLeaveTypeSearchTerm] = useState('');
  const leaveTypeSearchInputRef = useRef(null);

  // Modal states
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isAddLeaveTypeModalOpen, setIsAddLeaveTypeModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const fetchEmployees = useCallback(
    async (page = employeePage, search = employeeSearchTerm) => {
      try {
        const payload = await dispatch(
          getEmployees({
            page,
            per_page: 10,
            search: search || undefined,
            language: i18n.language,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setEmployeeOptions(items);
        setEmployeePagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('leaveRequests.add.messages.fetchEmployeesError', { defaultValue: 'فشل جلب الموظفين' }), { rtl: isRTL });
      }
    },
    [dispatch, t, i18n, isRTL]
  );

  useEffect(() => {
    // Fetch all leave types without pagination
    dispatch(getLeaveTypes({ per_page: 1000 }));
  }, [dispatch]);

  // Fetch data when modals open
  useEffect(() => {
    if (isAddEmployeeModalOpen) {
      dispatch(getDepartments());
      dispatch(getPositions());
      dispatch(getWorkShifts());
    }
  }, [isAddEmployeeModalOpen, dispatch]);

  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployees(employeePage, employeeSearchTerm);
    }
  }, [employeeDropdownOpen, employeePage, fetchEmployees]);

  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployees(1, employeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [employeeSearchTerm, employeeDropdownOpen, fetchEmployees]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (!success) return;

    // Check if success is from modals (more specific checks)
    const lowerSuccess = success.toLowerCase();

    // More specific check for modal success (employee or leave type/vacation type)
    const isModalEmployeeSuccess = (
      success.includes('موظف') ||
      lowerSuccess.includes('employee') && !lowerSuccess.includes('request')
    );

    const isModalLeaveTypeSuccess = (
      success.includes('إجازة') ||
      lowerSuccess.includes('vacation type') ||
      lowerSuccess.includes('leave type') ||
      (lowerSuccess.includes('vacation') && !lowerSuccess.includes('request'))
    );

    const isModalSuccess = isModalEmployeeSuccess || isModalLeaveTypeSuccess;

    // Check if it's specifically a leave request success (not leave type)
    const isLeaveRequestSuccess = (
      lowerSuccess.includes('leave request') ||
      lowerSuccess.includes('طلب إجازة') ||
      (lowerSuccess.includes('تم إضافة') && lowerSuccess.includes('طلب'))
    );

    if (isModalSuccess) {
      // Success from modals - show toast from API response, don't navigate
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      return;
    } else if (isLeaveRequestSuccess) {
      // Success from adding leave request - show toast and navigate
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      setTimeout(() => {
        navigate('/leave-requests');
      }, 1000);
    } else {
      // Unknown success - just show toast, don't navigate (to be safe)
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
      const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setEmployeeDropdownOpen(false);
        setLeaveTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.leave_type_id || !formData.start_date || !formData.end_date) {
      toast.error(t('leaveRequests.add.validation.requiredFields'), { rtl: isRTL });
      return;
    }

    // Validate dates
    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);

    if (startDate >= endDate) {
      toast.error(t('leaveRequests.add.validation.invalidDates'), { rtl: isRTL });
      return;
    }

    await dispatch(addLeaveRequest(formData));
  };

  const handleCancel = () => {
    navigate('/leave-requests');
  };

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      overflow: 'visible'
    }}>

      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/leave-requests')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowLeft />
          {t('leaveRequests.add.back')}
        </button>

        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'white',
          margin: 0,
          textAlign
        }}>
          {t('leaveRequests.add.title')}
        </h1>
      </div>


      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '30px',
        border: '1px solid #333',
        overflow: 'visible',
        position: 'relative'
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
            {/* العمود الأيسر */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              {/* الموظف */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{
                    color: 'white',
                    fontWeight: '500',
                    textAlign,
                    margin: 0
                  }}>
                    {t('leaveRequests.add.fields.employee')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddEmployeeModalOpen(true)}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    <FaPlus style={{ fontSize: '10px' }} />
                    {t('leaveRequests.add.buttons.addEmployee')}
                  </button>
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setLeaveTypeDropdownOpen(false);
                      setEmployeeDropdownOpen((prev) => !prev);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <span>
                      {formData.employee_id
                        ? (() => {
                          const selected = employeeOptions.find((e) => String(e.id) === String(formData.employee_id));
                          return selected
                            ? `${(isRTL ? selected.name_ar : selected.name_en) || selected.name} - ${selected.employee_code}`
                            : t('leaveRequests.add.placeholders.selectEmployee');
                        })()
                        : t('leaveRequests.add.placeholders.selectEmployee')}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <DropdownWithSearch
                    isOpen={employeeDropdownOpen}
                    searchTerm={employeeSearchTerm}
                    onSearchChange={setEmployeeSearchTerm}
                    searchInputRef={employeeSearchInputRef}
                    items={employeeOptions}
                    pagination={employeePagination}
                    onPageChange={setEmployeePage}
                    onClose={() => setEmployeeDropdownOpen(false)}
                    onSelect={(item) => {
                      setFormData(prev => ({ ...prev, employee_id: String(item.id) }));
                      setEmployeeDropdownOpen(false);
                      setEmployeeSearchTerm('');
                      setEmployeePage(1);
                    }}
                    label={t('leaveRequests.add.fields.employee')}
                    isRTL={isRTL}
                    i18n={i18n}
                  />
                </div>
              </div>


              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{
                    color: 'white',
                    fontWeight: '500',
                    textAlign,
                    margin: 0
                  }}>
                    {t('leaveRequests.add.fields.leaveType')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddLeaveTypeModalOpen(true)}
                    style={{
                      backgroundColor: '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    <FaPlus style={{ fontSize: '10px' }} />
                    {t('leaveRequests.add.buttons.addLeaveType')}
                  </button>
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setEmployeeDropdownOpen(false);
                      setLeaveTypeDropdownOpen((prev) => {
                        if (!prev) {
                          setLeaveTypeSearchTerm('');
                        }
                        return !prev;
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <span>
                      {formData.leave_type_id
                        ? (() => {
                          const selected = leaveTypes?.find((lt) => String(lt.id) === String(formData.leave_type_id));
                          return selected
                            ? selected.name
                            : t('leaveRequests.add.placeholders.selectLeaveType');
                        })()
                        : t('leaveRequests.add.placeholders.selectLeaveType')}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  <SimpleDropdown
                    isOpen={leaveTypeDropdownOpen}
                    items={leaveTypes || []}
                    searchTerm={leaveTypeSearchTerm}
                    onSearchChange={setLeaveTypeSearchTerm}
                    searchInputRef={leaveTypeSearchInputRef}
                    onClose={() => {
                      setLeaveTypeDropdownOpen(false);
                      setLeaveTypeSearchTerm('');
                    }}
                    onSelect={(item) => {
                      setFormData(prev => ({ ...prev, leave_type_id: String(item.id) }));
                      setLeaveTypeDropdownOpen(false);
                      setLeaveTypeSearchTerm('');
                    }}
                    selectedValue={formData.leave_type_id}
                    isRTL={isRTL}
                    dir={dir}
                    textAlign={textAlign}
                    searchPlaceholder={t('leaveRequests.add.placeholders.searchLeaveType', { defaultValue: 'ابحث عن نوع إجازة...' })}
                  />
                </div>
              </div>
            </div>


            <div style={{ flex: '1', minWidth: '300px' }}>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('leaveRequests.add.fields.startDate')}
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    colorScheme: 'dark',
                    direction: dir
                  }}
                />
              </div>


              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'white',
                  fontWeight: '500',
                  textAlign
                }}>
                  {t('leaveRequests.add.fields.endDate')}
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    colorScheme: 'dark',
                    direction: dir
                  }}
                />
              </div>
            </div>
          </div>


          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: 'white',
              fontWeight: '500',
              textAlign
            }}>
              {t('leaveRequests.add.fields.reason')}
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              placeholder={t('leaveRequests.add.placeholders.reason')}
              rows="4"
              style={{
                width: '100%',
                padding: '12px 15px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                resize: 'vertical',
                direction: dir,
                textAlign
              }}
            />
          </div>


          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '30px',
            justifyContent: 'center',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isLoading ? 0.6 : 1,
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {t('leaveRequests.add.buttons.adding')}
                </>
              ) : (
                <>
                  <FaPlus />
                  {t('leaveRequests.add.buttons.add')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              {t('leaveRequests.add.buttons.cancel')}
            </button>
          </div>
        </form>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Add Employee Modal */}
      {isAddEmployeeModalOpen && ReactDOM.createPortal(
        <AddEmployeeModal
          isOpen={isAddEmployeeModalOpen}
          onClose={() => {
            setIsAddEmployeeModalOpen(false);
            dispatch(clearSuccess());
            dispatch(clearError());
          }}
          onSuccess={(employeeId) => {
            setFormData(prev => ({ ...prev, employee_id: String(employeeId) }));
            setIsAddEmployeeModalOpen(false);
            fetchEmployees(employeePage, employeeSearchTerm);
          }}
          t={t}
          isRTL={isRTL}
          dir={dir}
          textAlign={textAlign}
          dispatch={dispatch}
          departments={departments}
          positions={positions}
          workShifts={workShifts}
          i18n={i18n}
        />,
        document.body
      )}

      {/* Add Leave Type Modal */}
      {isAddLeaveTypeModalOpen && ReactDOM.createPortal(
        <AddLeaveTypeModal
          isOpen={isAddLeaveTypeModalOpen}
          onClose={() => {
            setIsAddLeaveTypeModalOpen(false);
            dispatch(clearSuccess());
            dispatch(clearError());
          }}
          onSuccess={(leaveTypeId) => {
            setFormData(prev => ({ ...prev, leave_type_id: String(leaveTypeId) }));
            setIsAddLeaveTypeModalOpen(false);
            dispatch(getLeaveTypes({ per_page: 1000 }));
          }}
          t={t}
          isRTL={isRTL}
          dir={dir}
          textAlign={textAlign}
          dispatch={dispatch}
          isLoading={isLoading}
        />,
        document.body
      )}
    </div>
  );
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 10000,
  padding: 12,
  boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
  overflow: 'hidden',
  maxHeight: '550px',
  display: 'flex',
  flexDirection: 'column',
};

const simpleDropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 10000,
  padding: '12px',
  boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
  maxHeight: '550px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const SimpleDropdown = ({
  isOpen,
  items,
  searchTerm = '',
  onSearchChange,
  searchInputRef,
  onClose,
  onSelect,
  selectedValue,
  isRTL = true,
  dir = 'rtl',
  textAlign = 'right',
  searchPlaceholder,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchInputRef]);

  // Filter items based on search term
  const filteredItems = searchTerm
    ? items.filter((item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name_en?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : items;

  if (!isOpen) return null;

  return (
    <div style={{ ...simpleDropdownMenuStyle, direction: dir }} onClick={(e) => e.stopPropagation()} data-dropdown-menu>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={searchPlaceholder || t('leaveRequests.add.placeholders.searchLeaveType', { defaultValue: 'ابحث عن نوع إجازة...' })}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange?.(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dir, textAlign }}
        />
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: '150px' }}>
        {filteredItems.length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dir, textAlign }}>
            {t('leaveRequests.add.pagination.noResults')}
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dir,
                textAlign,
                color: 'white',
                backgroundColor: String(item.id) === String(selectedValue) ? '#2a2a2a' : 'transparent',
                marginBottom: index === filteredItems.length - 1 ? '16px' : '0'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = String(item.id) === String(selectedValue) ? '#2a2a2a' : 'transparent';
              }}
            >
              {item.name}
            </div>
          ))
        )}
        <div style={{ height: '40px' }}></div>
      </div>
    </div>
  );
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
  itemDisplayKey = 'name',
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={searchPlaceholder || t('leaveRequests.add.placeholders.searchEmployee')}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
              {t('leaveRequests.add.pagination.page')} {pagination.current_page} {t('leaveRequests.add.pagination.of')} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ««
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }}
              disabled={pagination.current_page === 1}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ‹
            </button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPageChange(page); }}
                  style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}
                >
                  {page}
                </button>
              ));
            })()}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              ›
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }}
              disabled={pagination.current_page === pagination.last_page}
              style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}
            >
              »»
            </button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: '400px', overflowY: 'auto', flex: 1, minHeight: '150px' }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>
            {t('leaveRequests.add.pagination.noResults')}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map((item, index) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left',
                color: 'white',
                marginBottom: index === (Array.isArray(items) ? items : []).length - 1 ? '16px' : '0'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2a2a2a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {item[itemDisplayKey] || (dropdownIsRTL ? item.name_ar : item.name_en) || item.name} {item.employee_code ? `- ${item.employee_code}` : ''}
            </div>
          ))
        )}
        <div style={{ height: '40px' }}></div>
      </div>
    </div>
  );
};

// Add Employee Modal Component
const AddEmployeeModal = ({ isOpen, onClose, onSuccess, t, isRTL, dir, textAlign, dispatch, departments, positions, workShifts, i18n }) => {
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    username: '',
    password: '',
    password_confirmation: '',
    email: '',
    phone: '',
    employee_code: '',
    national_id: '',
    birth_date: '',
    gender: 'male',
    department_id: '',
    position_id: '',
    work_shift_id: '',
    role_id: '',
    is_active: true,
    can_start_tasks_without_approval: false,
  });
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolePage, setRolePage] = useState(1);
  const [rolePagination, setRolePagination] = useState(null);
  const roleSearchInputRef = useRef(null);
  const [usernameStatus, setUsernameStatus] = useState('idle'); // idle | checking | available | exists | error
  const [usernameMessage, setUsernameMessage] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const usernameCheckTimeoutRef = useRef(null);
  const { success, error, isLoading } = useSelector((state) => state.auth);

  const normalizeListResponse = (payload) => {
    if (!payload) return { items: [], pagination: null };

    let items = [];
    let pagination = null;

    if (Array.isArray(payload?.data?.data)) {
      items = payload.data.data;
      pagination = payload.data?.meta || payload.data?.pagination || null;
    } else if (Array.isArray(payload?.data)) {
      items = payload.data;
      pagination = payload.meta || payload.pagination || null;
    } else if (Array.isArray(payload?.items)) {
      items = payload.items;
      pagination = payload.pagination || payload.meta || null;
    } else if (Array.isArray(payload)) {
      items = payload;
      pagination = null;
    } else {
      items = [];
      pagination = payload.pagination || payload.meta || null;
    }

    if (pagination) {
      pagination = {
        current_page: pagination.current_page || pagination.page || 1,
        last_page: pagination.last_page || pagination.total_pages || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }

    return { items, pagination };
  };

  const fetchRoles = useCallback(
    async (page = rolePage, search = roleSearchTerm) => {
      try {
        const payload = await dispatch(
          getRoles({
            page,
            per_page: 10,
            search: search || undefined,
          })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setRoleOptions(items);
        setRolePagination(paginationMeta);
      } catch (error) {
        toast.error(error || t('employees.form.messages.fetchRolesError', { defaultValue: 'فشل جلب الأدوار' }), { rtl: isRTL });
      }
    },
    [dispatch, t, isRTL]
  );

  useEffect(() => {
    if (roleDropdownOpen) {
      fetchRoles(rolePage, roleSearchTerm);
    }
  }, [roleDropdownOpen, rolePage, fetchRoles]);

  useEffect(() => {
    if (roleSearchTerm !== undefined && roleDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setRolePage(1);
        fetchRoles(1, roleSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [roleSearchTerm, roleDropdownOpen, fetchRoles]);

  // Success is handled in parent component's useEffect, this can be removed

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
    }
  }, [error, isRTL]);

  const isUsernameAvailable = async (username) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        return false;
      }
      const response = await axios.post(
        `${BASE_URL}/auth/check-username`,
        { username },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': currentLang,
          },
        }
      );
      const exists = response?.data?.data?.exists;
      return !exists;
    } catch {
      return false;
    }
  };

  const generateUsernameSuggestions = async (baseUsername) => {
    const suggestions = [];
    let attempt = 1;
    const maxAttempts = 30;

    while (suggestions.length < 3 && attempt <= maxAttempts) {
      const candidate = `${baseUsername}${attempt}`;
      // Avoid suggesting the same username
      if (candidate !== baseUsername && !(await isUsernameAvailable(candidate))) {
        attempt += 1;
        continue;
      }
      if (!suggestions.includes(candidate)) {
        suggestions.push(candidate);
      }
      attempt += 1;
    }

    setUsernameSuggestions(suggestions);
  };

  const checkUsernameAvailability = async (username, withSuggestions = false) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        setUsernameStatus('error');
        setUsernameMessage(t('employees.form.validation.requiredFields'));
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/auth/check-username`,
        { username },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': currentLang,
          },
        }
      );
      const exists = response?.data?.data?.exists;
      if (exists) {
        setUsernameStatus('exists');
        setUsernameMessage(t('employees.form.usernameMessages.exists'));
        if (withSuggestions) {
          await generateUsernameSuggestions(username);
        }
      } else {
        setUsernameStatus('available');
        setUsernameMessage(t('employees.form.usernameMessages.available', { username }));
        setUsernameSuggestions([]);
      }
    } catch {
      setUsernameStatus('error');
      setUsernameMessage(t('employees.form.usernameMessages.exists'));
    }
  };

  const handleUsernameSuggestionClick = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      username: suggestion,
    }));
    setUsernameSuggestions([]);
    checkUsernameAvailability(suggestion, false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'can_start_tasks_without_approval') {
      setFormData(prev => ({ ...prev, [name]: value === '1' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'username') {
      const username = value.trim();
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
      if (!username) {
        setUsernameStatus('idle');
        setUsernameMessage('');
        setUsernameSuggestions([]);
        return;
      }
      setUsernameStatus('checking');
      setUsernameMessage(t('employees.form.usernameMessages.checking'));
      setUsernameSuggestions([]);
      usernameCheckTimeoutRef.current = setTimeout(() => {
        checkUsernameAvailability(username, true);
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.phone || !formData.birth_date) {
      toast.error(t('employees.form.validation.requiredFields'), { rtl: isRTL });
      return;
    }

    const trimmedUsername = (formData.username || '').trim();
    const hasUsername = trimmedUsername.length > 0;

    if (hasUsername) {
      if (!formData.password || !formData.password_confirmation) {
        toast.error(t('employees.form.validation.passwordRequired', { defaultValue: 'Password and password confirmation are required when entering a username' }), { rtl: isRTL });
        return;
      }

      if (formData.password !== formData.password_confirmation) {
        toast.error(t('employees.form.validation.passwordMismatch', { defaultValue: 'Password and password confirmation do not match' }), { rtl: isRTL });
        return;
      }

      // Prevent submit if we already know username exists
      if (usernameStatus === 'exists') {
        toast.error(t('employees.form.usernameMessages.exists'), { rtl: isRTL });
        return;
      }

      // Final check on submit to avoid race conditions
      const available = await isUsernameAvailable(trimmedUsername);
      if (!available) {
        setUsernameStatus('exists');
        setUsernameMessage(t('employees.form.usernameMessages.exists'));
        await generateUsernameSuggestions(trimmedUsername);
        toast.error(t('employees.form.usernameMessages.exists'), { rtl: isRTL });
        return;
      }
    }

    const formDataToSend = new FormData();

    Object.keys(formData).forEach(key => {
      if (!hasUsername && (key === 'password' || key === 'password_confirmation')) {
        return;
      }
      if (!hasUsername && key === 'username') {
        return;
      }

      if (formData[key] !== null && formData[key] !== undefined) {
        if (key === 'is_active' || key === 'can_start_tasks_without_approval') {
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else if (key === 'department_id' || key === 'position_id' || key === 'work_shift_id' || key === 'role_id') {
          formDataToSend.append(key, formData[key] || '');
        } else if (key === 'email') {
          const emailValue = formData[key].trim();
          formDataToSend.append(key, emailValue === '' ? 'null' : emailValue);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    formDataToSend.set('can_start_tasks_without_approval', formData.can_start_tasks_without_approval ? '1' : '0');

    if (formData.email === '' || (formData.email && formData.email.trim() === '')) {
      formDataToSend.set('email', 'null');
    }

    if (hasUsername) {
      formDataToSend.set('username', trimmedUsername);
    }

    try {
      const result = await dispatch(addEmployee(formDataToSend)).unwrap();
      if (result?.data?.id) {
        // Refresh employees list and get the new employee
        dispatch(getEmployees({ page: 1, per_page: 1 })).then((employeeResult) => {
          const items = employeeResult.payload?.data?.data || employeeResult.payload?.data || [];
          if ((Array.isArray(items) ? items : []).length > 0) {
            onSuccess(items[0].id);
          }
        });

        // Reset form and username status
        setFormData({
          name_en: '',
          name_ar: '',
          username: '',
          password: '',
          password_confirmation: '',
          email: '',
          phone: '',
          employee_code: '',
          national_id: '',
          birth_date: '',
          gender: 'male',
          department_id: '',
          position_id: '',
          work_shift_id: '',
          role_id: '',
          is_active: true,
          can_start_tasks_without_approval: false,
        });
        setUsernameStatus('idle');
        setUsernameMessage('');
        setUsernameSuggestions([]);

        // Don't show toast here - it will come from Redux success message in parent component
        // Close modal after a short delay (toast from API will show automatically)
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (error) {
      // Error is handled by Redux and will show via error state
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      padding: 16
    }} onClick={onClose}>
      <div style={{
        background: '#1a1f2e',
        borderRadius: '12px',
        padding: '30px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        direction: dir
      }} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            [isRTL ? 'left' : 'right']: '1rem',
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '24px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
        <h2 style={{ marginTop: 0, marginBottom: 20, color: 'white', textAlign }}>{t('leaveRequests.add.modals.addEmployee.title')}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.nameAr')}</label>
              <input type="text" name="name_ar" value={formData.name_ar} onChange={handleInputChange} placeholder={t('employees.form.placeholders.nameAr')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', direction: 'rtl', textAlign: 'right' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.nameEn')}</label>
              <input type="text" name="name_en" value={formData.name_en} onChange={handleInputChange} placeholder={t('employees.form.placeholders.nameEn')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', direction: 'ltr', textAlign: 'left' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.employeeCode')}</label>
              <input type="text" name="employee_code" value={formData.employee_code} onChange={handleInputChange} placeholder={t('employees.form.placeholders.employeeCode')} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.username')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span></label>
              <input type="text" name="username" value={formData.username} onChange={handleInputChange} placeholder={t('employees.form.placeholders.username')} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
              {usernameMessage && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color:
                      usernameStatus === 'available'
                        ? '#10b981'
                        : usernameStatus === 'checking'
                          ? '#eab308'
                          : '#f97373',
                  }}
                >
                  {usernameMessage}
                </div>
              )}
              {usernameSuggestions.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div
                    style={{
                      marginBottom: '4px',
                      fontSize: '12px',
                      color: '#9ca3af',
                    }}
                  >
                    {t('employees.form.usernameMessages.suggestionsTitle')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {usernameSuggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => handleUsernameSuggestionClick(suggestion)}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: '#374151',
                          borderRadius: '999px',
                          border: 'none',
                          color: 'white',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {formData.username.trim() && (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.password', { defaultValue: 'Password' })}</label>
                  <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder={t('employees.form.placeholders.password', { defaultValue: 'Enter password' })} required={formData.username.trim().length > 0} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.passwordConfirmation', { defaultValue: 'Confirm Password' })}</label>
                  <input type="password" name="password_confirmation" value={formData.password_confirmation} onChange={handleInputChange} placeholder={t('employees.form.placeholders.passwordConfirmation', { defaultValue: 'Re-enter password' })} required={formData.username.trim().length > 0} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
                </div>
              </>
            )}
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.nationalId')}</label>
              <input type="text" name="national_id" value={formData.national_id} onChange={handleInputChange} placeholder={t('employees.form.placeholders.nationalId')} maxLength={14} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.email')} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span></label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={t('employees.form.placeholders.email')} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.phone')}</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t('employees.form.placeholders.phone')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.birthDate')}</label>
              <input type="date" name="birth_date" value={formData.birth_date} onChange={handleInputChange} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.gender')}</label>
              <select name="gender" value={formData.gender} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.genderPlaceholder')}</option>
                <option value="male" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.gender.male')}</option>
                <option value="female" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.common.gender.female')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.department')}</label>
              <select name="department_id" value={formData.department_id} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.departmentPlaceholder')}</option>
                {departments?.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.position')}</label>
              <select name="position_id" value={formData.position_id} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.positionPlaceholder')}</option>
                {positions?.map(pos => <option key={pos.id} value={pos.id}>{pos.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.workPlace')}</label>
              <select name="work_shift_id" value={formData.work_shift_id} onChange={handleInputChange} style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="">{t('employees.form.placeholders.workPlace')}</option>
                {workShifts?.map(shift => <option key={shift.id} value={shift.id}>{shift.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.role', { defaultValue: 'الدور' })} <span style={{ color: 'white', fontSize: '12px', fontWeight: 'normal' }}>{t('employees.form.optional')}</span></label>
              <div style={{ position: 'relative' }} data-dropdown-container>
                <div
                  data-dropdown-toggle
                  onClick={() => setRoleDropdownOpen((prev) => !prev)}
                  style={{
                    width: '100%',
                    padding: 10,
                    backgroundColor: '#14263b',
                    border: '1px solid #333',
                    borderRadius: 6,
                    color: 'white',
                    fontSize: '14px',
                    direction: dir,
                    textAlign,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <span>
                    {formData.role_id
                      ? (() => {
                        const selected = roleOptions.find((r) => String(r.id) === String(formData.role_id));
                        return selected
                          ? selected.name
                          : t('employees.form.placeholders.selectRole', { defaultValue: 'اختر الدور' });
                      })()
                      : t('employees.form.placeholders.selectRole', { defaultValue: 'اختر الدور' })}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={roleDropdownOpen}
                  searchTerm={roleSearchTerm}
                  onSearchChange={setRoleSearchTerm}
                  searchInputRef={roleSearchInputRef}
                  items={roleOptions}
                  pagination={rolePagination}
                  onPageChange={setRolePage}
                  onClose={() => setRoleDropdownOpen(false)}
                  onSelect={(item) => {
                    setFormData(prev => ({ ...prev, role_id: String(item.id) }));
                    setRoleDropdownOpen(false);
                    setRoleSearchTerm('');
                    setRolePage(1);
                  }}
                  label={t('employees.form.fields.role', { defaultValue: 'الدور' })}
                  isRTL={isRTL}
                  i18n={i18n}
                  itemDisplayKey="name"
                  searchPlaceholder={t('employees.form.placeholders.searchRole', { defaultValue: 'ابحث عن دور...' })}
                />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.status')}</label>
              <select
                name="is_active"
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  is_active: e.target.value === 'active'
                }))}
                style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}
              >
                <option value="active" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.statusOptions.active')}</option>
                <option value="inactive" style={{ backgroundColor: '#1a1f2e' }}>{t('employees.form.statusOptions.inactive')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('employees.form.fields.canStartTasks', { defaultValue: 'Can start tasks without approval' })}</label>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="can_start_tasks_without_approval"
                    value="1"
                    checked={formData.can_start_tasks_without_approval === true}
                    onChange={handleInputChange}
                    style={{ cursor: 'pointer' }}
                  />
                  {t('employees.common.yes', { defaultValue: 'Yes' })}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="can_start_tasks_without_approval"
                    value="0"
                    checked={formData.can_start_tasks_without_approval === false}
                    onChange={handleInputChange}
                    style={{ cursor: 'pointer' }}
                  />
                  {t('employees.common.no', { defaultValue: 'No' })}
                </label>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center' }}>
            <button type="submit" disabled={isLoading} style={{ backgroundColor: isLoading ? '#666' : '#AC2000', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
              {t('employees.form.buttons.submitAdd')}
            </button>
            <button type="button" onClick={onClose} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer' }}>
              {t('employees.form.buttons.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Leave Type Modal Component
const AddLeaveTypeModal = ({ isOpen, onClose, onSuccess, t, isRTL, dir, textAlign, dispatch, isLoading }) => {
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    default_balance: '',
    is_active: false
  });
  const { success, error } = useSelector((state) => state.auth);

  // Success handling is now done directly in handleSubmit
  // This useEffect can be removed or kept for backward compatibility
  useEffect(() => {
    if (success && success.includes('إجازة')) {
      // Success is already handled in handleSubmit, but we can clear it here if needed
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
    }
  }, [error, isRTL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? value === 'true' : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.default_balance) {
      toast.error(t('vacations.add.validation.requiredFields'), { rtl: isRTL });
      return;
    }

    const submitData = {
      ...formData,
      default_balance: parseInt(formData.default_balance)
    };

    try {
      const result = await dispatch(addLeaveType(submitData)).unwrap();
      if (result?.data?.id) {
        // Refresh leave types list and get the new leave type - use result.data.id directly
        onSuccess(String(result.data.id));
        // Also refresh the leave types dropdown in parent
        dispatch(getLeaveTypes({ per_page: 1000 }));

        // Reset form
        setFormData({
          name_en: '',
          name_ar: '',
          default_balance: '',
          is_active: false
        });

        // Don't show toast here - it will come from Redux success message
        // Close modal after a short delay (toast from API will show automatically)
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (error) {
      // Error is handled by Redux and will show via error state
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      padding: 16
    }} onClick={onClose}>
      <div style={{
        background: '#1a1f2e',
        borderRadius: '12px',
        padding: '30px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        direction: dir
      }} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            [isRTL ? 'left' : 'right']: '1rem',
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '24px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
        <h2 style={{ marginTop: 0, marginBottom: 20, color: 'white', textAlign }}>{t('leaveRequests.add.modals.addLeaveType.title')}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('vacations.add.fields.nameAr')}</label>
              <input type="text" name="name_ar" value={formData.name_ar} onChange={handleInputChange} placeholder={t('vacations.add.placeholders.nameAr')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', direction: 'rtl', textAlign: 'right' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('vacations.add.fields.nameEn')}</label>
              <input type="text" name="name_en" value={formData.name_en} onChange={handleInputChange} placeholder={t('vacations.add.placeholders.nameEn')} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', direction: 'ltr', textAlign: 'left' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('vacations.add.fields.defaultBalance')}</label>
              <input type="number" name="default_balance" value={formData.default_balance} onChange={handleInputChange} placeholder={t('vacations.add.placeholders.defaultBalance')} required min="0" style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'white', textAlign }}>{t('vacations.add.fields.isActive')}</label>
              <select name="is_active" value={formData.is_active ? 'true' : 'false'} onChange={handleInputChange} required style={{ width: '100%', padding: 10, backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white' }}>
                <option value="false">{t('vacations.add.fields.inactive')}</option>
                <option value="true">{t('vacations.add.fields.active')}</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'center' }}>
            <button type="submit" disabled={isLoading} style={{ backgroundColor: isLoading ? '#666' : '#AC2000', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
              {t('vacations.add.buttons.add')}
            </button>
            <button type="button" onClick={onClose} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer' }}>
              {t('vacations.add.buttons.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLeaveRequest;

